import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  CloudUpload,
  Download,
  Loader2,
  Plus,
  RefreshCcw,
  SearchX,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { stripFilterIds } from "@/components/companies/AdvancedFilters";
import { FilterChipsBar } from "@/components/companies/FilterChipsBar";
import { AddFilterPopover } from "@/components/companies/AddFilterPopover";
import { AddSortButton } from "@/components/companies/AddSortButton";
import {
  getFilterDisplayString,
  getSortDisplayString,
  TAB_ORDER,
  TAB_SPECS,
  type TabKey,
} from "@/components/companies/filterTypes";
import { UnifiedFilterRow } from "@/components/companies/UnifiedFilterRow";
import type { ColumnSizingState } from "@tanstack/react-table";
import {
  CompanyTable,
  CompanyTableSkeleton,
} from "@/components/companies/CompanyTable";
import { ClientsTable } from "@/components/companies/ClientsTable";
import { BulkActionsBar } from "@/components/companies/BulkActionsBar";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { ArchiveCompanyDialog } from "@/components/companies/ArchiveCompanyDialog";
import { CreateCompanyDialog } from "@/components/companies/CreateCompanyDialog";
import { CfoFundingSection } from "@/components/companies/CfoFundingSection";
import { ImportClientsDialog } from "@/components/companies/ImportClientsDialog";
import { CallLogForm } from "@/components/activity/CallLogForm";
import { EnrollDialog } from "@/components/companies/EnrollDialog";
import {
  exportCompaniesCSV,
  getCompany,
  listCompanies,
  rescoreAllCompanies,
} from "@/api/companies";
import { listMembers } from "@/api/projects";
import { listContacts } from "@/api/contacts";
import { useAuthStore } from "@/stores/auth.store";
import { useScoringStore } from "@/stores/scoring.store";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";
import { useCompanyColumns } from "@/hooks/useCompanyColumns";
import { useCompanyFilterChips, useCompanyFilterState } from "@/hooks/useCompanyFilters";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import { useColumnPreferences } from "@/hooks/useColumnPreferences";
// F3-bis dropped useDefaultAllView — the synthetic "All" view no
// longer exists frontend-side. Default-all rows still live in the
// DB until a separate cleanup PR; the UI just ignores them.
import { ColumnSelector } from "@/components/table/ColumnSelector";
import {
  readCompanyViewState,
} from "@/lib/companyViewState";
import { formatNumber } from "@/lib/formatters";
import type {
  Company,
  CompanyDetail,
  Contact,
  ProjectMember,
  SavedViewEntity,
  SavedViewRead,
} from "@/api/types";
import { getEffectivePipelineStatuses } from "@/lib/projectConfig";
// F6 split ViewsBar into two surfaces: a dropdown (ViewsDropdown)
// in the new row 2 and a Save / Discard cluster (ViewActionsCluster)
// rendered alongside the chips bar in row 5.
import { ViewsDropdown } from "@/components/views/ViewsDropdown";
import { ViewActionsCluster } from "@/components/views/ViewActionsCluster";
import { SaveAsNewViewModal } from "@/components/views/SaveAsNewViewModal";
import { filtersEqual, useViews } from "@/hooks/useViews";

// `Tab` now carries an explicit "all" value — the new pristine
// reset target. The 3 pre-existing values keep their semantics 1:1
// against the listFilters branching below.
type Tab = TabKey;

const PAGE_SIZE = 50;

/** The pristine "All" view — no saved view, no filters, default
 *  pagination. Single source of truth for both the initial page
 *  state and the global "Clear filters" action so the two can't
 *  drift apart. Column *visibility* isn't here — it lives in the
 *  useColumnPreferences hook, reset via its own `resetColumns()`.
 *
 *  Note `tab: "all"` here — the pristine reset clears the implicit
 *  scope entirely. First-page-load default is still "prospects"
 *  (see `useState<Tab>("prospects")` below) because new users land
 *  in the working pipeline, not the unscoped reset state. */
const ALL_VIEW_DEFAULTS = {
  tab: "all" as Tab,
  columnSizing: {} as ColumnSizingState,
  page: 1,
};

/** One-shot localStorage cleanup for any stale entries referencing
 *  the synthetic "All" view (sentinel ids "__all__" or "all") from
 *  builds that pre-date F3-bis. Silent — a malformed entry shouldn't
 *  crash the page, and a clean migration is invisible to the user. */
function migrateAwayFromSyntheticAll(): void {
  try {
    const keysToScan: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith("prism.")) keysToScan.push(k);
    }
    for (const k of keysToScan) {
      const v = localStorage.getItem(k);
      if (!v) continue;
      if (v === "__all__" || v === "all") {
        localStorage.removeItem(k);
        continue;
      }
      // JSON blobs that embedded the sentinel inside an
      // activeViewId field — strip the field rather than nuke
      // the whole blob (it may carry useful column prefs).
      if (v.startsWith("{") || v.startsWith("[")) {
        try {
          const parsed = JSON.parse(v);
          if (
            parsed &&
            typeof parsed === "object" &&
            !Array.isArray(parsed) &&
            (parsed.activeViewId === "__all__" || parsed.activeViewId === "all")
          ) {
            delete (parsed as Record<string, unknown>).activeViewId;
            localStorage.setItem(k, JSON.stringify(parsed));
          }
        } catch {
          /* Malformed JSON — leave the entry as-is; not our problem. */
        }
      }
    }
  } catch {
    /* Quota / private mode / etc. — defensive, never throws. */
  }
}

type LoadStatus = "loading" | "ok" | "error";

export default function CompaniesPage() {
  const { project } = useCurrentProject();
  const perms = usePermissions();
  const confirm = useConfirm();
  const scoringEnabled = useFeature("scoring");
  const lemlistEnabled = useFeature("lemlist");
  const {
    canCreate,
    canEdit,
    canImport,
    canExportCompanies,
    canEditScoring: canEditScoringPerm,
    canRescoreAll: canRescoreAllPerm,
    canArchive,
    canViewTags,
    canCreateViews,
    canShareViews,
    canBulkUpdateCompanies,
    canBulkDeleteCompanies,
  } = perms;
  // Score column / scoring actions also need the project's scoring
  // feature flag — toggling scoring off in Settings → Features
  // hides the column and disables rescore on this page.
  const canViewScore = perms.canViewScore && scoringEnabled;
  const canEditScoring = canEditScoringPerm && scoringEnabled;
  // Whole-project rescore lives behind its own permission (granted
  // to admins by default in the seed). Gated on the same scoring
  // feature flag as the column / per-row rescore.
  const canRescoreAll = canRescoreAllPerm && scoringEnabled;
  const currentUser = useAuthStore((s) => s.user);
  const slug = project?.slug ?? null;

  const isScoringDirty = useScoringStore((s) =>
    slug ? Boolean(s.dirtyByProject[slug]) : false,
  );
  const setScoringDirty = useScoringStore((s) => s.setDirty);
  const [rescoring, setRescoring] = useState(false);

  const filterState = useCompanyFilterState("prospects");
  const {
    tab,
    setTab,
    search,
    setSearch,
    debouncedSearch,
    advancedFilters,
    advancedFiltersParam,
    sorts,
    setSorts,
    sortsParam,
    listFilters,
    entity,
    anyFilterActive,
    applyView,
    resetFilters,
    addFilter: handleAddFilter,
    removeFilter: handleRemoveFilter,
    editFilter: handleEditFilter,
  } = filterState;
  const [page, setPage] = useState(ALL_VIEW_DEFAULTS.page);

  // One-shot localStorage cleanup at mount — strips any stale
  // synthetic-All references from pre-F3-bis builds. Idempotent and
  // silent; rerunning costs one full localStorage scan but doesn't
  // produce duplicate state.
  useEffect(() => {
    migrateAwayFromSyntheticAll();
  }, []);

  // F6: parent owns the SaveAsNewViewModal open state so both the
  // ViewsDropdown's "+ Create new view" footer button AND the
  // ViewActionsCluster's chevron can trigger the same modal
  // instance. Without this, each trigger would render its own
  // modal and the dropdown popover would stack on top of the
  // modal during the transition.
  const [saveAsNewOpen, setSaveAsNewOpen] = useState(false);

  // Per-column widths shared by both tables (column ids don't
  // collide between Prospects and Clients). The saved view is the
  // sole persistence layer — applyViewFilters restores from
  // view.filters.column_sizing, the Save path serializes back into
  // currentFiltersJson, and a user with no active view stays on
  // defaults until they save.
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    () => ({ ...ALL_VIEW_DEFAULTS.columnSizing }),
  );

  const [items, setItems] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  // Gates the first companies fetch — flips true once the auto-apply
  // effect below has either applied a default view OR confirmed there
  // isn't one. Without this, the table briefly fetches with an empty
  // filter set and then re-fetches when the view's filters land a
  // tick later. Per-tab: a separate effect resets it when the entity
  // (= tab) changes so the new tab's view also lands before its
  // first fetch.
  const [viewApplied, setViewApplied] = useState(false);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const showSkeleton = useDelayedLoading(status === "loading", 200);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Gmail-style "select all matching" — sticky flag set when the
  // operator promotes the page-level selection to every matching row
  // across pages. The bulk-action bar dispatches on this: when true,
  // the server resolves the row set from the filter tree (mode=all_
  // matching) so the client doesn't have to paginate ids. `selectedIds`
  // tracks visible-page ids just to keep the checkbox visuals correct.
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [contactsByCompanyId, setContactsByCompanyId] = useState<
    Record<number, Contact | null>
  >({});
  const [contactsLoading, setContactsLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Company | null>(null);
  const [logTarget, setLogTarget] = useState<CompanyDetail | null>(null);
  const [logLoadingId, setLogLoadingId] = useState<number | null>(null);
  const [enrollTarget, setEnrollTarget] = useState<CompanyDetail | null>(null);
  const [enrollLoadingId, setEnrollLoadingId] = useState<number | null>(null);

  const pipelineStatuses = getEffectivePipelineStatuses(project);

  // Per-project-type "won" status key — drives the Create dialog
  // default when the operator is on the Clients tab. Falls back to
  // an empty string only when no status carries the flag (then the
  // dialog will still open with the project's initial status).
  const wonStatusKey = useMemo(
    () => pipelineStatuses.find((s) => s.is_won)?.key ?? "",
    [pipelineStatuses],
  );


  // Reset page on any filter / search / tab / sort change.
  useEffect(() => {
    setPage(1);
  }, [tab, debouncedSearch, advancedFiltersParam, sortsParam]);

  // Reset selection whenever the underlying result set changes: tab
  // switch, filter edit, new search, or sort change. The user's
  // expectation is that "select all matching" follows the filters
  // they can see — carrying it across a filter change would
  // silently apply actions to companies that aren't on screen any
  // more.
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  }, [tab, debouncedSearch, advancedFiltersParam, sortsParam]);

  // Fetch members once per project.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    listMembers(slug)
      .then((m) => !cancelled && setMembers(m))
      .catch(() => !cancelled && setMembers([]));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Available columns from the project's project_type, without the
  // perm-gated ones. The column selector + table both read from this.
  const availableColumns = useCompanyColumns(slug, { canViewScore, canViewTags });

  const load = useCallback(async () => {
    if (!slug) return;
    setStatus("loading");
    setErrorMessage(null);
    try {
      const res = await listCompanies(slug, {
        ...listFilters,
        page,
        page_size: PAGE_SIZE,
      });
      setItems(res.items);
      setTotal(res.total);
      setStatus("ok");
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
      setStatus("error");
    }
  }, [slug, page, listFilters]);

  useEffect(() => {
    // Don't fire the first companies fetch until we've checked for a
    // default view to apply. Otherwise the table briefly shows
    // unfiltered data, then re-fetches when the view's filters land
    // a tick later. `viewApplied` flips true at the end of the auto-
    // apply effect below regardless of whether a view was actually
    // applied, so projects without a default view aren't blocked.
    if (!viewApplied) return;
    void load();
  }, [load, viewApplied]);

  // Fetch first contact per client (Clients tab only — the
  // ClientsTable surfaces it as a column).
  useEffect(() => {
    if (tab !== "clients" || !slug || items.length === 0) {
      setContactsByCompanyId({});
      return;
    }
    let cancelled = false;
    setContactsLoading(true);
    Promise.all(
      items.map(async (item) => {
        try {
          const res = await listContacts(slug, {
            company_id: item.id,
            page_size: 1,
          });
          return [item.id, res.items[0] ?? null] as const;
        } catch {
          return [item.id, null] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setContactsByCompanyId(Object.fromEntries(entries));
      setContactsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [tab, slug, items]);

  // Everything the server returned — no client-side filtering left
  // after the unified-filter refactor.
  const visibleItems = items;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  };

  // Promote the page-level selection to every matching row. Just
  // flips the flag — the bulk endpoints' mode=all_matching takes the
  // filter tree directly so we don't paginate ids client-side. A
  // separate effect below mirrors visible-page ids into selectedIds so
  // the row checkboxes stay visually correct.
  function selectAllMatchingNow() {
    setSelectAllMatching(true);
  }

  // Filter tree fed to bulk-action mode=all_matching. Mirrors the
  // listCompanies params so the server re-evaluates exactly the same
  // result set, with the advanced builder's conditions inlined (the
  // GET endpoint takes a JSON string; the POST endpoint takes the
  // object directly).
  const allMatchingFilters = useMemo<Record<string, unknown>>(() => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(listFilters)) {
      if (v === undefined || v === null) continue;
      // Strip the GET endpoint's JSON-string `filters`; the POST
      // takes them inline as a `conditions` array re-attached below.
      if (k === "filters") continue;
      out[k] = v;
    }
    if (advancedFilters.length > 0) {
      out.conditions = stripFilterIds(advancedFilters);
    }
    return out;
  }, [listFilters, advancedFilters]);

  // Keep visible-page ids in selectedIds while in all_matching mode so
  // checkboxes render checked. The bulk action ignores selectedIds in
  // this mode, so the set is purely cosmetic. Adds only — never
  // removes — so prior pages stay visually selected if the user
  // backs up.
  useEffect(() => {
    if (!selectAllMatching) return;
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const c of visibleItems) {
        if (!next.has(c.id)) {
          next.add(c.id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectAllMatching, visibleItems]);

  // User-driven selection changes from the table. Decreasing the set
  // while in all_matching mode means "I unchecked something" — drop
  // out of the mode so the bulk action falls back to the explicit ids
  // (Gmail behavior).
  const handleSelectionChange = useCallback(
    (next: Set<number>) => {
      if (selectAllMatching && next.size < selectedIds.size) {
        setSelectAllMatching(false);
      }
      setSelectedIds(next);
    },
    [selectAllMatching, selectedIds.size],
  );

  async function rescoreAllNow() {
    if (!slug) return;
    setRescoring(true);
    try {
      const res = await rescoreAllCompanies(slug);
      setScoringDirty(slug, false);
      toast.success(
        `Rescore started for ${res.total_companies.toLocaleString()} companies. Scores will update shortly.`,
      );
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setRescoring(false);
    }
  }

  // Header-button variant: same job as `rescoreAllNow`, plus an
  // explicit confirm. The banner version above triggers right after
  // the operator just edited scoring rules, so a confirm there would
  // be friction; the header button can fire any time, so we prompt.
  async function confirmAndRescoreAll() {
    if (!slug || rescoring) return;
    const ok = await confirm({
      title: "Rescore All Companies",
      description:
        "Rescore all companies in this project? This may take a few minutes.",
      confirmLabel: "Rescore All",
      variant: "info",
      icon: <RefreshCcw className="h-5 w-5" />,
    });
    if (!ok) return;
    await rescoreAllNow();
  }

  async function handleExport() {
    if (!slug) return;
    setExporting(true);
    try {
      // Same filter dispatch as the listing — shared via listFilters
      // so the CSV can't diverge from what's on screen.
      await exportCompaniesCSV(slug, listFilters);
      const noun = total === 1 ? "company" : "companies";
      toast.success(`CSV exported (${formatNumber(total)} ${noun})`);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        toast.error("Too many rows. Apply filters to reduce.");
      } else {
        toast.error("Export failed. Try again.");
      }
    } finally {
      setExporting(false);
    }
  }

  function switchTab(next: Tab) {
    if (next === tab) return;
    setTab(next);
    // Per the unified-filters design, filters travel with the operator
    // across tabs — switching tab only changes the implicit is_client
    // scope. Selection resets via its own filter-change effect.
  }

  async function handleAskLogInteraction(c: Company) {
    if (!slug || logLoadingId === c.id) return;
    setLogLoadingId(c.id);
    try {
      const detail = await getCompany(slug, c.id);
      setLogTarget(detail);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setLogLoadingId(null);
    }
  }

  async function handleAskEnroll(c: Company) {
    if (!slug || enrollLoadingId === c.id) return;
    setEnrollLoadingId(c.id);
    try {
      const detail = await getCompany(slug, c.id);
      setEnrollTarget(detail);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setEnrollLoadingId(null);
    }
  }

  // ─── Saved views ──────────────────────────────────────────────────────────
  // Close the load gate whenever the tab changes — the new entity's
  // views haven't been auto-applied yet, and we don't want to fetch
  // the wrong filter set while the auto-apply effect catches up.
  useEffect(() => {
    setViewApplied(false);
  }, [entity]);

  const viewsApi = useViews(slug, entity);
  const {
    views,
    isLoading: viewsLoading,
    loaded: viewsLoaded,
    defaultView,
    create: createView,
    rename: renameView,
    setShared: setViewShared,
    patch: patchView,
    remove: deleteView,
    toggleDefault,
  } = viewsApi;

  // F3-bis removed the synthetic "All" view entirely. The
  // useDefaultAllView hook is gone; old default-all rows live on
  // in the DB until a separate cleanup PR but the UI never reads
  // them.

  // The current filter state, shaped for the active tab. Used for both
  // active-view detection and persistence into a saved view.
  //
  // Filter keys are snake_case to match the backend API params (and the seed
  // data). `owner_id` uses the literal "me" marker when it points at the
  // current user, so a view saved by Romaric ("show MY leads") still means
  // "show MY leads" when Sophie clicks it — instead of fixing on Romaric's
  // UUID. Real UUIDs (a teammate explicitly picked) round-trip unchanged.
  const currentFiltersJson = useMemo<Record<string, unknown>>(() => {
    return {
      search: search.trim(),
      // Local ids are random UUIDs — strip before persisting so a
      // round-tripped view doesn't churn its filter equality check.
      advanced_filters: stripFilterIds(advancedFilters),
      // Piggyback per-column widths into the saved-view JSON. The
      // backend treats `filters` as a loose Record and round-trips it
      // verbatim; only emit when there's a customization, so the
      // canonical filtersEqual check doesn't churn on `{}` vs missing.
      ...(Object.keys(columnSizing).length > 0
        ? { column_sizing: columnSizing }
        : {}),
      // Sort state (F3-bis-frontend). Only emit when non-empty so
      // the canonical filtersEqual check doesn't churn on `[]` vs
      // missing — same conditional-spread idiom as column_sizing.
      ...(sorts.length > 0 ? { sorts } : {}),
    };
  }, [search, advancedFilters, columnSizing, sorts]);

  // Active view is tracked by intent (the user picked it) rather than
  // derived from filter equality, so tweaking a filter doesn't drop the
  // selection. The dirty flag below tells us when state has drifted from
  // the saved filters.
  const [activeViewId, setActiveViewId] = useState<number | null>(null);
  const activeView = useMemo<SavedViewRead | null>(
    () => views.find((v) => v.id === activeViewId) ?? null,
    [views, activeViewId],
  );
  // Column preferences scoped per tab so each tab keeps its own
  // localStorage bucket and surfaces its own dirty state. Prospects
  // wires into the unified `prism.{slug}.companies.viewState` blob
  // (which also stores filters); Clients let the hook persist its
  // own `prism.{slug}.companies-clients.columns` key.
  const initialColumnsFromStorage = useMemo<string[] | null>(() => {
    if (!slug) return null;
    return readCompanyViewState(slug)?.columns ?? null;
  }, [slug]);
  // "all" tab shares the prospects bucket for column-pref storage —
  // same reasoning as the saved-view entity above.
  const scope =
    tab === "clients"
      ? "companies-clients"
      : tab === "archived"
        ? "companies-archived"
        : "companies";
  // Column baseline: a named view when one is active, else the
  // customized "All" (or null → backend defaults) on the "All" tab.
  // useColumnPreferences already accepts a SavedViewRead | null, and
  // the customized "All" from /views/default-all *is* a SavedViewRead
  // — so it's passed straight through, no synthetic-object API needed.
  // F3-bis: no default-all fallback. When no view is active, column
  // preferences resolve from the backend defaults.
  const columnBaselineView = activeView;
  const {
    activeKeys: visibleColumnKeys,
    isDirty: columnsDirty,
    setActiveKeys: setVisibleColumnKeys,
    reset: resetColumns,
    resetToDefaults: resetColumnsToDefaults,
  } = useColumnPreferences({
    slug,
    scope,
    available: availableColumns,
    // The baseline flows into the hook on every tab so its dirty
    // check tracks the right reference — a named view's columns, the
    // customized "All"'s columns, or the backend defaults.
    activeView: columnBaselineView,
    // Only Prospects feeds initial columns from the unified viewState
    // blob — Clients uses the hook's own per-tab key.
    initialActiveKeys:
      tab === "prospects" ? initialColumnsFromStorage : undefined,
    persistToLocalStorage: tab !== "prospects",
  });

  // The view is considered dirty when either filters OR columns drift
  // from the saved value. ViewsBar's "View has changes" indicator and
  // the Save / Reset buttons key off this combined flag.
  const filtersDirty = useMemo<boolean>(
    () =>
      !!activeView && !filtersEqual(activeView.filters, currentFiltersJson),
    [activeView, currentFiltersJson],
  );
  const isDirty = filtersDirty || (!!activeView && columnsDirty);

  // The current view differs from the pristine "All" default when a
  // saved view is active, any filter is set (search / advanced /
  // score — score lives in advancedFilters), column visibility has
  // drifted, or a column has been resized. Drives the global
  // "Clear filters" button's visibility.
  const showClearFilters =
    activeViewId !== null ||
    anyFilterActive ||
    columnsDirty ||
    Object.keys(columnSizing).length > 0;

  const applyViewFilters = useCallback((view: SavedViewRead) => {
    applyView(view);
    // Restore the per-column widths, if the view carried them. An
    // absent / non-object value resets to defaults so an old view
    // without sizing doesn't lock in stale widths from another view.
    // Named views embed sizing in `filters.column_sizing`; the
    // backend also surfaces a first-class `column_sizing` — read the
    // embedded one first, fall back to the top-level field.
    const f = view.filters as Record<string, unknown>;
    const sizing = f.column_sizing ?? view.column_sizing;
    if (sizing && typeof sizing === "object" && !Array.isArray(sizing)) {
      const next: ColumnSizingState = {};
      for (const [k, v] of Object.entries(sizing as Record<string, unknown>)) {
        if (typeof v === "number" && Number.isFinite(v) && v > 0) next[k] = v;
      }
      setColumnSizing(next);
    } else {
      setColumnSizing({});
    }
  }, [applyView]);

  function resetAllFilters() {
    resetFilters();
    setColumnSizing({ ...ALL_VIEW_DEFAULTS.columnSizing });
  }

  function handlePickView(view: SavedViewRead) {
    setActiveViewId(view.id);
    applyViewFilters(view);
  }

  /** The absolute zero state — pristine ALL_VIEW_DEFAULTS, backend
   *  default columns, page 1. Shared by the global "Clear filters"
   *  button and "Reset to defaults". Uses resetColumnsToDefaults
   *  (not resetColumns) so it reaches the pristine column set even
   *  when the column baseline is a customized "All". One batched
   *  event handler → one render → the load effect fires once.
   *
   *  Also resets the tab to "all" — pristine means "no implicit
   *  scope at all". First-page-load is the only path that keeps
   *  the legacy "prospects" default; clearing filters explicitly
   *  drops to the unscoped view. */
  /** Global "Clear filters" — resets everything to ALL_VIEW_DEFAULTS.
   *  Wired to the toolbar's "Clear filters" button. F3-bis renamed
   *  from `applyPristineAll` to match the user-facing button label
   *  now that there's no synthetic "All" view to confuse it with. */
  function clearAllFilters() {
    setActiveViewId(null);
    setTab(ALL_VIEW_DEFAULTS.tab);
    resetAllFilters();
    resetColumnsToDefaults();
    setPage(ALL_VIEW_DEFAULTS.page);
  }

  function handleClearFilters() {
    clearAllFilters();
  }

  function handleResetActiveView() {
    if (!activeView) return;
    // Reset filters back to the saved view…
    applyViewFilters(activeView);
    // …and reset columns. The hook keys off `activeView` and re-resolves
    // its baseline (view.columns when present, otherwise defaults), so
    // calling reset() here picks the view's saved column list.
    resetColumns();
  }

  async function handleUpdateActiveView() {
    if (!activeView) return;
    try {
      // PATCH both filters and columns together so the user's "Save"
      // captures the full state, not just one half.
      await patchView(activeView.id, {
        filters: currentFiltersJson,
        columns: visibleColumnKeys,
      });
      toast.success(`View "${activeView.name}" updated`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  // F3-bis: the legacy-blob → default-all migration effect is gone.
  // Stale prism.{slug}.companies.viewState blobs from older builds
  // remain in localStorage but useColumnPreferences GCs them on its
  // next write — no migration target to promote them to.

  // Auto-apply the landing view once the tab's views have settled.
  // Tracked per entity so re-visiting a tab during the same session
  // doesn't wipe hand-picked filters. A named is_default view wins;
  // otherwise the page stays in its first-load pristine state (raw
  // D2 — tab=Prospects, no chips except the implicit Is-client one).
  const autoAppliedRef = useRef<Set<SavedViewEntity>>(new Set());
  useEffect(() => {
    if (!viewsLoaded) return;
    if (autoAppliedRef.current.has(entity)) {
      setViewApplied(true);
      return;
    }
    autoAppliedRef.current.add(entity);
    if (defaultView) {
      applyViewFilters(defaultView);
      setActiveViewId(defaultView.id);
    }
    // else: pristine raw state — initial useState defaults already hold.
    setViewApplied(true);
  }, [viewsLoaded, entity, defaultView, applyViewFilters]);

  // If the active view is deleted (e.g. by another teammate, or by us in a
  // shared bar), drop the selection so we don't carry a phantom ID.
  useEffect(() => {
    if (activeViewId !== null && !views.some((v) => v.id === activeViewId)) {
      setActiveViewId(null);
    }
  }, [views, activeViewId]);

  // ─── Filter chips (F1) ─────────────────────────────────────────────────
  // The tab chip, then one chip per condition — the same chips the Email
  // wizard shows for the same filters.
  const {
    pickerFieldList,
    activeFilters,
    resolveCondition,
    resolveField,
  } = useCompanyFilterChips(filterState, { availableColumns, pipelineStatuses, members });

  return (
    <div className="space-y-6 pb-24">
      <header className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Companies
            </h1>
            {status === "ok" && (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                {formatNumber(total)}{" "}
                {tab === "prospects"
                  ? "prospects"
                  : tab === "clients"
                    ? "clients"
                    : "archived"}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">
            {project?.name ?? "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {slug && project?.cfo_effective_scoring && (canImport || perms.canViewEngine) && (
            <CfoFundingSection key={slug} slug={slug} canImport={canImport} canConfirm={canImport && perms.canTriggerEngine} />
          )}
          {tab === "clients" && canImport && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setImportOpen(true)}
            >
              <CloudUpload className="mr-2 h-3.5 w-3.5" />
              Import clients
            </Button>
          )}
          {canExportCompanies && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || status !== "ok" || total === 0 || !slug}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="mr-2 h-3.5 w-3.5" />
              )}
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          )}
          {canRescoreAll && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void confirmAndRescoreAll()}
              disabled={rescoring || !slug}
              title="Recompute every company's score using the project's current scoring rules."
            >
              {rescoring ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-3.5 w-3.5" />
              )}
              {rescoring ? "Rescoring…" : "Rescore all"}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={status === "loading" || !slug}
          >
            <RefreshCcw
              className={
                status === "loading"
                  ? "mr-2 h-3.5 w-3.5 animate-spin"
                  : "mr-2 h-3.5 w-3.5"
              }
            />
            Refresh
          </Button>
          {canCreate && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCreateOpen(true)}
              disabled={!slug}
            >
              <Plus className="mr-2 h-3.5 w-3.5" />
              New company
            </Button>
          )}
        </div>
      </header>

      {isScoringDirty && tab === "prospects" && (
        <ScoringDirtyBanner
          canRescore={canEditScoring}
          rescoring={rescoring}
          onRescore={rescoreAllNow}
        />
      )}

      <Tabs value={tab} onValueChange={(v) => switchTab(v as Tab)}>
        <TabsList>
          {/* Order is fixed in TAB_ORDER — All sits leftmost as the
           *  pristine reset target. First-page-load default stays
           *  "prospects" (useState above) so existing operators
           *  keep landing on the working pipeline. */}
          {TAB_ORDER.map((key) => (
            <TabsTrigger key={key} value={key}>
              {TAB_SPECS[key].label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Row 2 (F6): Views dropdown + View settings dropdown. The
       *  horizontal pill row that used to sit here was replaced
       *  by a single Popover-backed dropdown — fits more views
       *  gracefully, surfaces a search input, and frees toolbar
       *  width. ViewActionsCluster (Discard / Save / Save as new)
       *  was relocated to row 5 alongside the chips bar — closer
       *  to the chips it's about to persist. */}
      <div className="flex items-center gap-2">
        <ViewsDropdown
          views={views}
          loading={viewsLoading}
          activeView={activeView}
          canCreate={canCreateViews}
          canShare={canShareViews}
          onPickView={handlePickView}
          onRename={async (view, name) => {
            await renameView(view.id, name);
          }}
          onToggleShared={async (view) => {
            await setViewShared(view.id, !view.is_shared);
          }}
          onToggleDefault={async (view) => {
            await toggleDefault(view.id, view.is_default);
          }}
          onDelete={async (view) => {
            await deleteView(view.id);
          }}
          onCreateNewClick={() => setSaveAsNewOpen(true)}
        />
        <ColumnSelector
          available={availableColumns}
          activeKeys={visibleColumnKeys}
          onChange={setVisibleColumnKeys}
          onReset={resetColumns}
          isCustomized={columnsDirty}
          disabled={availableColumns.length === 0}
          triggerLabel="View settings"
        />
      </div>

      <UnifiedFilterRow
        slug={slug ?? ""}
        search={search}
        onSearchChange={setSearch}
        onClearFilters={handleClearFilters}
        showClearFilters={showClearFilters}
        // Zero-chips state: the toolbar still needs a way in to the
        // +Add filter popover. Once at least one chip is active, the
        // inline +Add filter inside the chips bar takes over and
        // this slot returns null to avoid duplication.
        addFilterSlot={
          activeFilters.length === 0 ? (
            <AddFilterPopover
              fields={pickerFieldList}
              slug={slug ?? ""}
              members={members}
              pipelineStatuses={pipelineStatuses}
              onAdd={handleAddFilter}
              variant="toolbar"
            />
          ) : null
        }
        // Toolbar "Sort" button — ALWAYS rendered, no `sorts.length`
        // gate. F4-bis ensures the popover anchor stays mounted
        // across sorts mutations.
        addSortSlot={
          <AddSortButton
            sorts={sorts}
            onSortsChange={setSorts}
            variant="toolbar"
          />
        }
      />

      {/* Row 5: chips bar + right-edge ViewActionsCluster (F6
       *  relocation from the views row). The cluster only renders
       *  when a named view is dirty — same gating as in F3-bis.
       *  Wrapped in a flex container so both children sit on the
       *  same row, cluster pushed right by ml-auto. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <FilterChipsBar
          activeFilters={activeFilters}
          resolveField={resolveField}
          resolveCondition={resolveCondition}
          pickerFields={pickerFieldList}
          slug={slug ?? ""}
          members={members}
          pipelineStatuses={pipelineStatuses}
          sorts={sorts}
          onSortsChange={setSorts}
          onEditFilter={handleEditFilter}
          onRemoveFilter={handleRemoveFilter}
          onAddFilter={handleAddFilter}
        />
        <div className="ml-auto">
          <ViewActionsCluster
            activeView={activeView}
            isDirty={isDirty}
            canCreate={canCreateViews}
            canShare={canShareViews}
            savingActive={false}
            previewItems={activeFilters.map((f) => getFilterDisplayString(f))}
            sortPreview={getSortDisplayString(sorts)}
            onUpdateActive={handleUpdateActiveView}
            onResetActive={handleResetActiveView}
            onCreateView={async ({ name, is_shared }) => {
              const created = await createView({
                name,
                filters: currentFiltersJson,
                columns: visibleColumnKeys,
                is_shared,
              });
              setActiveViewId(created.id);
              return created;
            }}
          />
        </div>
      </div>

      {/* F6: page-owned SaveAsNewViewModal. Driven by saveAsNewOpen,
       *  triggered by the ViewsDropdown's "+ Create new view" button
       *  (the cluster's chevron also opens its own internal modal
       *  instance — separate concern). Hidden trigger button —
       *  the controlled-props mode in SaveAsNewViewModal skips it. */}
      <SaveAsNewViewModal
        open={saveAsNewOpen}
        onOpenChange={setSaveAsNewOpen}
        canShare={canShareViews}
        previewItems={activeFilters.map((f) => getFilterDisplayString(f))}
        sortPreview={getSortDisplayString(sorts)}
        onSave={async ({ name, is_shared }) => {
          const created = await createView({
            name,
            filters: currentFiltersJson,
            columns: visibleColumnKeys,
            is_shared,
          });
          setActiveViewId(created.id);
          return created;
        }}
      />

      <SelectionBanner
        allCurrentPageSelected={
          visibleItems.length > 0 &&
          visibleItems.every((c) => selectedIds.has(c.id))
        }
        visibleCount={visibleItems.length}
        total={total}
        selectAllMatching={selectAllMatching}
        onSelectAllMatching={selectAllMatchingNow}
        onClear={clearSelection}
      />

      {status === "error" && (
        <ErrorBanner message={errorMessage} onRetry={load} />
      )}

      {status === "loading" && items.length === 0 && showSkeleton ? (
        <motion.div
          key="company-skeleton"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <CompanyTableSkeleton
            rows={8}
            showScore={canViewScore}
            showTags={canViewTags}
            selectable={canEdit || canArchive}
          />
        </motion.div>
      ) : status === "loading" && items.length === 0 ? null : visibleItems.length === 0 ? (
        <EmptyState
          tab={tab}
          anyFilterActive={anyFilterActive}
          canImport={canImport}
          onImport={() => setImportOpen(true)}
          onGoToProspects={() => switchTab("prospects")}
        />
      ) : tab === "prospects" || tab === "archived" ? (
        <>
          <CompanyTable
            items={visibleItems}
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            currentUser={currentUser}
            members={members}
            pipelineStatuses={pipelineStatuses}
            showScore={canViewScore}
            showTags={canViewTags}
            projectSlug={slug ?? ""}
            selectable={canEdit || canArchive}
            // On the Archived tab the "Archive" affordance doesn't
            // make sense — the rows are already archived. Bulk
            // restore lives in BulkActionsBar; per-row restore is
            // a separate enhancement and out of scope for this fix.
            onAskArchive={
              canArchive && tab !== "archived"
                ? (c) => setArchiveTarget(c)
                : undefined
            }
            onAskEnroll={
              canEdit && lemlistEnabled
                ? (c) => void handleAskEnroll(c)
                : undefined
            }
            visibleColumnKeys={
              availableColumns.length > 0 ? visibleColumnKeys : undefined
            }
            availableColumns={availableColumns}
            columnSizing={columnSizing}
            onColumnSizingChange={setColumnSizing}
            sorts={sorts}
            onSortsChange={setSorts}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            onChange={setPage}
          />
        </>
      ) : (
        <>
          <ClientsTable
            items={visibleItems}
            contactsByCompanyId={contactsByCompanyId}
            contactsLoading={contactsLoading}
            members={members}
            projectSlug={slug ?? ""}
            showTags={canViewTags}
            selectable={canEdit || canArchive}
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            onAskArchive={canArchive ? (c) => setArchiveTarget(c) : undefined}
            onAskLogInteraction={(c) => void handleAskLogInteraction(c)}
            visibleColumnKeys={
              availableColumns.length > 0 ? visibleColumnKeys : undefined
            }
            availableColumns={availableColumns}
            currentUser={currentUser}
            columnSizing={columnSizing}
            onColumnSizingChange={setColumnSizing}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            onChange={setPage}
          />
        </>
      )}

      {slug &&
        (canBulkUpdateCompanies ||
          (canArchive && tab !== "archived") ||
          (canBulkDeleteCompanies && tab === "archived")) && (
          <BulkActionsBar
            projectSlug={slug}
            pipelineStatuses={pipelineStatuses}
            members={members}
            selectedIds={Array.from(selectedIds)}
            currentUser={currentUser}
            selectAllMatching={selectAllMatching}
            allMatchingFilters={allMatchingFilters}
            totalMatching={total}
            canEdit={canBulkUpdateCompanies}
            // Archive is hidden on the Archived tab — rows are
            // already archived, so the affordance would be a
            // no-op. Delete takes its slot.
            canArchive={canArchive && tab !== "archived"}
            // Delete is ONLY exposed on the Archived tab — anywhere
            // else, the Archive flow is the safer default.
            // Permanently deleting a row is gated behind a typed-
            // count confirm inside BulkActionsBar.
            canDelete={canBulkDeleteCompanies && tab === "archived"}
            onClear={clearSelection}
            onSuccess={load}
          />
        )}

      {slug && (
        <ImportClientsDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          projectSlug={slug}
          onImported={load}
        />
      )}

      {slug && (
        <CreateCompanyDialog
          projectSlug={slug}
          open={createOpen}
          onOpenChange={setCreateOpen}
          pipelineStatuses={pipelineStatuses}
          defaultStatus={tab === "clients" ? wonStatusKey : "new"}
          onCreated={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}

      {slug && (
        <ArchiveCompanyDialog
          mode="single"
          projectSlug={slug}
          open={!!archiveTarget}
          onOpenChange={(o) => {
            if (!o) setArchiveTarget(null);
          }}
          company={archiveTarget}
          onArchived={() => {
            setArchiveTarget(null);
            void load();
          }}
        />
      )}

      {slug && logTarget && (
        <CallLogForm
          open={!!logTarget}
          onOpenChange={(o) => {
            if (!o) setLogTarget(null);
          }}
          projectSlug={slug}
          company={logTarget}
          pipelineStatuses={pipelineStatuses}
          onLogged={() => {
            setLogTarget(null);
            void load();
          }}
        />
      )}

      {slug && enrollTarget && (
        <EnrollDialog
          open={!!enrollTarget}
          onOpenChange={(o) => {
            if (!o) setEnrollTarget(null);
          }}
          projectSlug={slug}
          company={enrollTarget}
          onEnrolled={() => {
            setEnrollTarget(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

interface PaginationProps {
  page: number;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  onChange: (page: number) => void;
  note?: string | null;
}

function Pagination({
  page,
  totalPages,
  rangeStart,
  rangeEnd,
  total,
  onChange,
  note,
}: PaginationProps) {
  return (
    <div className="flex items-center justify-between text-xs text-slate-500">
      <div className="space-x-2">
        <span>
          <span className="font-medium text-slate-700">
            {rangeStart}–{rangeEnd}
          </span>{" "}
          of {formatNumber(total)}
        </span>
        {note && <span className="text-slate-400">· {note}</span>}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <span className="px-2 text-slate-600">
          {page} / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function EmptyState({
  tab,
  anyFilterActive,
  canImport,
  onImport,
  onGoToProspects,
}: {
  tab: Tab;
  anyFilterActive: boolean;
  canImport: boolean;
  onImport: () => void;
  onGoToProspects: () => void;
}) {
  const noun =
    tab === "prospects"
      ? "prospects"
      : tab === "clients"
        ? "clients"
        : "archived companies";
  if (anyFilterActive) {
    return (
      <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
        <SearchX className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <p className="text-base font-medium text-slate-700">
          No {noun} match these filters
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Try widening your filters or clearing them.
        </p>
      </div>
    );
  }
  if (tab === "clients") {
    return (
      <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
        <Briefcase className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <p className="text-base font-medium text-slate-700">No clients yet</p>
        <p className="mt-1 text-sm text-slate-500">
          Convert prospects to clients or import your existing client base.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          {canImport && (
            <Button variant="primary" size="sm" onClick={onImport}>
              <CloudUpload className="mr-2 h-3.5 w-3.5" />
              Import clients
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onGoToProspects}>
            Go to Prospects
          </Button>
        </div>
      </div>
    );
  }
  if (tab === "archived") {
    return (
      <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
        <p className="text-base font-medium text-slate-700">
          No archived companies
        </p>
        <p className="mt-1 text-sm text-slate-500">
          Companies you archive from Prospects or Clients will appear here.
        </p>
        <div className="mt-5 flex justify-center">
          <Button variant="outline" size="sm" onClick={onGoToProspects}>
            Go to Prospects
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-plane bg-white px-6 py-16 text-center shadow-plane">
      <p className="text-base font-medium text-slate-700">No prospects yet</p>
      <p className="mt-1 text-sm text-slate-500">
        Companies will appear here once the engine has run.
      </p>
    </div>
  );
}

// QuickStatusFilters (the horizontal pill row) was removed in F5
// alongside the score slider. Status filtering now flows through
// the "+ Add filter" picker like any other field; the resulting
// chip lives in the chips bar.

interface ScoringDirtyBannerProps {
  canRescore: boolean;
  rescoring: boolean;
  onRescore: () => void;
}

function ScoringDirtyBanner({
  canRescore,
  rescoring,
  onRescore,
}: ScoringDirtyBannerProps) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-plane border border-amber-200 bg-amber-50/60 px-5 py-3.5">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-amber-900">
            Scoring rules have changed
          </p>
          <p className="text-xs text-amber-700/90">
            The scores below still reflect the previous rules.
            {canRescore
              ? " Recalculate to apply the new weights."
              : " Ask an admin to recalculate the scores."}
          </p>
        </div>
      </div>
      {canRescore && (
        <Button
          type="button"
          size="sm"
          onClick={onRescore}
          disabled={rescoring}
          className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
          variant="outline"
        >
          {rescoring ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="mr-2 h-3.5 w-3.5" />
          )}
          Recalculate now
        </Button>
      )}
    </div>
  );
}

/** Gmail-style two-stage selection prompt:
 *  - When the current page is fully selected but the listing has
 *    more pages, offer to promote the selection to every matching
 *    company across pages.
 *  - When the selection already covers every matching company,
 *    show a "clear selection" affordance instead.
 *  Returns null in every other state so the table sits flush against
 *  the filter row when no banner is needed. */
function SelectionBanner({
  allCurrentPageSelected,
  visibleCount,
  total,
  selectAllMatching,
  onSelectAllMatching,
  onClear,
}: {
  allCurrentPageSelected: boolean;
  visibleCount: number;
  total: number;
  selectAllMatching: boolean;
  onSelectAllMatching: () => void;
  onClear: () => void;
}) {
  if (selectAllMatching) {
    // Count comes straight from the listCompanies response — no
    // pre-paginated id set to enumerate any more.
    return (
      <div className="flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm text-indigo-700">
        <span>
          All <span className="font-semibold">{formatNumber(total)}</span>{" "}
          companies matching the current filters are selected.
        </span>
        <button
          type="button"
          onClick={onClear}
          className="font-semibold underline-offset-2 hover:underline"
        >
          Clear selection
        </button>
      </div>
    );
  }
  if (allCurrentPageSelected && total > visibleCount) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm text-indigo-700">
        <span>
          All <span className="font-semibold">{formatNumber(visibleCount)}</span>{" "}
          companies on this page are selected.
        </span>
        <button
          type="button"
          onClick={onSelectAllMatching}
          className="font-semibold underline-offset-2 hover:underline"
        >
          Select all {formatNumber(total)} companies matching filters
        </button>
      </div>
    );
  }
  return null;
}

function ErrorBanner({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-rose-700">
          Couldn&rsquo;t load companies
        </p>
        {message && <p className="text-xs text-rose-600">{message}</p>}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="border-rose-200 bg-white text-rose-700 hover:bg-rose-100"
      >
        <RefreshCcw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
